import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

// WebSocket API for Charging Station Management (groups, locations, stations).
// Backend: super_server internal/cms/station_management.go. Every request type
// below is answered with "<type>_response" carrying { success, message, data }.

export function useStationManagementApi() {
  const { send } = useWebSocketContext();

  const call = useCallback(
    async (type, payload = {}) => {
      const res = await send(type, payload, { timeoutMs: 15000 });
      if (!res || res.type !== `${type}_response`) throw new Error("Invalid response from server");
      if (!res.success) throw new Error(res.message || "Request failed");
      return res.data;
    },
    [send]
  );

  return useMemo(
    () => ({
      listGroups: () => call("cms_list_groups"),
      saveGroup: (group) => call("cms_save_group", group),
      listLocations: (groupId = "") => call("cms_list_locations", { group_id: groupId }),
      saveLocation: (location) => call("cms_save_location", location),
      listStations: (groupId = "") => call("cms_list_managed_stations", { group_id: groupId }),
      saveStation: (station) => call("cms_save_station", station),
      setStatus: (entity, id, status) => call("cms_set_entity_status", { entity, id, status }),
      // maps.app.goo.gl short link -> full Google Maps URL (followed by the server)
      resolveMapsLink: (url) => call("cms_resolve_maps_link", { url }),
    }),
    [call]
  );
}

// Loads a list once (and again on refetch / when `deps` change).
// `fetcher` returns a promise of an array; pass null to skip loading.
export function useManagedList(fetcher, deps = []) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(fetcher));
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!fetcher) {
      setItems([]);
      setIsLoading(false);
      return [];
    }
    const id = ++requestRef.current;
    setIsLoading(true);
    setError("");
    try {
      const data = await fetcher();
      const list = Array.isArray(data) ? data : [];
      if (id === requestRef.current) setItems(list);
      return list;
    } catch (err) {
      if (id === requestRef.current) setError(err.message || "Failed to load");
      return [];
    } finally {
      if (id === requestRef.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { items, setItems, isLoading, error, refetch: load };
}
